import { create } from 'zustand';
import type { AppError, DownloadMode, SessionSnapshot } from '../core/types';
import { makeError, OneFlowError } from '../core/errors';
import { parseSourceUrl, sanitizePastedUrl } from '../core/url';
import type { SessionRecord } from '../engine/db';
import { Engine, NeedsUrl, type EngineEvent } from '../engine/engine';

export interface Settings {
  mode: DownloadMode;
  rememberSignedLinks: boolean;
  motion: 'system' | 'reduce' | 'full';
}

const DEFAULT_SETTINGS: Settings = {
  mode: 'balanced',
  rememberSignedLinks: false,
  motion: 'system',
};
const SETTINGS_KEY = 'oneflow.settings.v1';

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const p = JSON.parse(raw) as Partial<Settings>;
    return {
      mode: p.mode === 'preview' || p.mode === 'download' ? p.mode : 'balanced',
      rememberSignedLinks: p.rememberSignedLinks === true,
      motion: p.motion === 'reduce' || p.motion === 'full' ? p.motion : 'system',
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function saveSettings(s: Settings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  } catch {
    /* storage may be blocked; settings simply do not persist */
  }
}

export type Panel = null | 'details' | 'settings' | 'help' | 'cors' | 'install';

export interface Toast {
  id: number;
  kind: 'info' | 'success' | 'error';
  title: string;
  body?: string;
}

interface AppState {
  booted: boolean;
  bootError: AppError | null;
  records: Record<string, SessionRecord>;
  snapshots: Record<string, SessionSnapshot>;
  /** Byte offset each player or viewer is reading, for the coverage rail marker. */
  feeds: Record<string, number | null>;
  activeId: string | null;
  focus: boolean;
  queueOpen: boolean;
  panel: Panel;
  /** A session that can only continue after its link is pasted again. */
  linkNeeded: string | null;
  confirmRemove: string | null;
  confirmRestart: string | null;
  url: string;
  urlError: AppError | null;
  starting: boolean;
  toasts: Toast[];
  online: boolean;
  announcement: string;
  settings: Settings;

  boot(): Promise<void>;
  setUrl(v: string): void;
  submit(raw?: string): Promise<void>;
  setFeed(id: string, offset: number | null): void;
  setActive(id: string | null): void;
  setFocus(v: boolean): void;
  setPanel(p: Panel): void;
  setQueueOpen(v: boolean): void;
  resume(id: string, rawUrl?: string): Promise<void>;
  pause(id: string): void;
  remove(id: string): Promise<void>;
  restartFresh(id: string, rawUrl?: string): Promise<void>;
  setMode(id: string, mode: DownloadMode): void;
  updateSettings(patch: Partial<Settings>): void;
  toast(t: Omit<Toast, 'id'>): void;
  dismissToast(id: number): void;
  setLinkNeeded(id: string | null): void;
  setConfirmRemove(id: string | null): void;
  setConfirmRestart(id: string | null): void;
  announce(msg: string): void;
}

let toastId = 1;

export const useApp = create<AppState>((set, get) => ({
  booted: false,
  bootError: null,
  records: {},
  snapshots: {},
  feeds: {},
  activeId: null,
  focus: false,
  queueOpen: false,
  panel: null,
  linkNeeded: null,
  confirmRemove: null,
  confirmRestart: null,
  url: '',
  urlError: null,
  starting: false,
  toasts: [],
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  announcement: '',
  settings: loadSettings(),

  async boot() {
    if (get().booted) return;
    try {
      const recs = await engine.boot();
      const records: Record<string, SessionRecord> = {};
      for (const r of recs) records[r.id] = r;
      set({ booted: true, records });
    } catch (e) {
      set({
        booted: true,
        bootError: e instanceof OneFlowError ? e.appError : makeError('STORAGE_UNAVAILABLE'),
      });
    }
  },

  setUrl(v) {
    set({ url: v, urlError: null });
  },

  async submit(raw) {
    const text = raw ?? get().url;
    const parsed = parseSourceUrl(text);
    if (!parsed.ok) {
      set({ urlError: parsed.error });
      return;
    }
    set({ starting: true, urlError: null });
    try {
      const id = await engine.startNew(parsed.source);
      set({ url: '', activeId: id, starting: false, focus: false });
      get().announce('Download started');
    } catch (e) {
      set({
        starting: false,
        urlError: e instanceof OneFlowError ? e.appError : makeError('UNKNOWN'),
      });
    }
  },

  setFeed(id, offset) {
    set((s) => (s.feeds[id] === offset ? s : { feeds: { ...s.feeds, [id]: offset } }));
  },
  setActive(id) {
    set({ activeId: id, focus: false });
  },
  setFocus(v) {
    set({ focus: v });
  },
  setPanel(p) {
    set({ panel: p });
  },
  setQueueOpen(v) {
    set({ queueOpen: v });
  },

  async resume(id, rawUrl) {
    try {
      await engine.resume(id, rawUrl);
      set({ linkNeeded: null });
    } catch (e) {
      if (e instanceof NeedsUrl) {
        set({ linkNeeded: id });
        return;
      }
      const err = e instanceof OneFlowError ? e.appError : makeError('UNKNOWN');
      get().toast({ kind: 'error', title: err.summary, body: err.action });
    }
  },

  pause(id) {
    engine.pause(id);
  },

  async remove(id) {
    await engine.remove(id);
    set((s) => {
      const { [id]: _r, ...records } = s.records;
      const { [id]: _s, ...snapshots } = s.snapshots;
      return {
        records,
        snapshots,
        activeId: s.activeId === id ? null : s.activeId,
        confirmRemove: null,
        focus: false,
      };
    });
    get().announce('Download deleted');
  },

  async restartFresh(id, rawUrl) {
    try {
      await engine.restartFresh(id, rawUrl);
      set({ confirmRestart: null, linkNeeded: null });
    } catch (e) {
      if (e instanceof NeedsUrl) {
        set({ linkNeeded: id, confirmRestart: null });
        return;
      }
      const err = e instanceof OneFlowError ? e.appError : makeError('UNKNOWN');
      get().toast({ kind: 'error', title: err.summary, body: err.action });
    }
  },

  setMode(id, mode) {
    engine.setMode(id, mode);
    get().updateSettings({ mode });
  },

  updateSettings(patch) {
    const next = { ...get().settings, ...patch };
    saveSettings(next);
    set({ settings: next });
  },

  toast(t) {
    const id = toastId++;
    set((s) => ({ toasts: [...s.toasts.slice(-3), { ...t, id }] }));
    setTimeout(() => get().dismissToast(id), t.kind === 'error' ? 9000 : 5000);
  },
  dismissToast(id) {
    set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) }));
  },
  setLinkNeeded(id) {
    set({ linkNeeded: id });
  },
  setConfirmRemove(id) {
    set({ confirmRemove: id });
  },
  setConfirmRestart(id) {
    set({ confirmRestart: id });
  },
  announce(msg) {
    set({ announcement: msg });
  },
}));

/** The single engine for the app. Settings are read lazily when a session starts. */
export const engine = new Engine(() => {
  const s = useApp.getState().settings;
  return { mode: s.mode, rememberSignedLinks: s.rememberSignedLinks };
});

const lastState = new Map<string, string>();

function onEngineEvent(e: EngineEvent): void {
  const st = useApp.getState();
  switch (e.type) {
    case 'record': {
      const prev = lastState.get(e.record.id);
      lastState.set(e.record.id, e.record.state);
      useApp.setState((s) => ({ records: { ...s.records, [e.record.id]: e.record } }));
      if (prev !== undefined && prev !== e.record.state) {
        const name = e.record.name;
        if (e.record.state === 'complete') st.announce(`${name} finished downloading`);
        else if (e.record.state === 'paused') st.announce(`${name} paused`);
        else if (e.record.state === 'recoverableError' || e.record.state === 'fatalError') {
          st.announce(`${name}: ${e.record.lastError?.summary ?? 'stopped'}`);
        }
      }
      break;
    }
    case 'snapshot':
      useApp.setState((s) => ({ snapshots: { ...s.snapshots, [e.id]: e.snapshot } }));
      break;
    case 'removed':
      useApp.setState((s) => {
        const { [e.id]: _r, ...records } = s.records;
        const { [e.id]: _s, ...snapshots } = s.snapshots;
        return { records, snapshots };
      });
      break;
    case 'worker-crashed':
      st.toast({
        kind: 'error',
        title: makeError('WORKER_CRASHED').summary,
        body: makeError('WORKER_CRASHED').action,
      });
      break;
    case 'ready':
      break;
  }
}
engine.onEvent(onEngineEvent);

export { sanitizePastedUrl };
